import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Globe, Check, Loader2 } from "lucide-react";

/**
 * LanguageSelector — used on Discover / More page.
 * Fetches /api/languages, current selection from useAuth().user.language.
 * Persists via PATCH /api/users/me { language }.
 * Saves to localStorage as `hf_lang` for immediate UI use.
 */
export default function LanguageSelector() {
  const { user, refresh } = useAuth();
  const [langs, setLangs] = useState([]);
  const [saving, setSaving] = useState(null);
  const [current, setCurrent] = useState(user?.language || (typeof window !== "undefined" && localStorage.getItem("hf_lang")) || "en");

  useEffect(() => {
    api.get("/languages").then(r => setLangs(r.data)).catch(() => setLangs([]));
  }, []);

  useEffect(() => {
    if (user?.language) setCurrent(user.language);
  }, [user?.language]);

  const pick = async (code) => {
    if (code === current) return;
    setSaving(code);
    try {
      try { localStorage.setItem("hf_lang", code); } catch { /* noop */ }
      setCurrent(code);
      if (user?.id) {
        await api.patch("/users/me", { language: code });
        refresh?.();
      }
    } catch { /* noop */ }
    finally { setSaving(null); }
  };

  return (
    <div className="px-4 mt-5" data-testid="language-selector">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-5 h-5 text-[#00E5FF]"/>
        <h2 className="font-outfit font-bold text-lg">Language</h2>
        <span className="text-xs text-white/50">Choose your preferred language</span>
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="language-grid">
        {langs.map(l => {
          const on = current === l.code;
          return (
            <button
              key={l.code}
              onClick={() => pick(l.code)}
              disabled={saving === l.code}
              data-testid={`lang-${l.code}`}
              className={`relative px-2.5 py-2.5 rounded-2xl border text-left active:scale-95 transition ${on ? "bg-[#00E5FF]/15 border-[#00E5FF]/50" : "bg-white/5 border-white/10"}`}>
              <div className="text-sm font-outfit font-bold truncate">{l.native}</div>
              <div className="text-[10px] text-white/60 truncate">{l.name}</div>
              {on && (
                <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#00E5FF] flex items-center justify-center">
                  {saving === l.code ? <Loader2 className="w-2.5 h-2.5 text-black animate-spin"/> : <Check className="w-2.5 h-2.5 text-black" strokeWidth={4}/>}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
